// @ts-strict-ignore
import { IProjectHelper, IResponsibleForProjectTypeHelper } from '@project/helper/i-project.helper';
import { Connection } from '@3d-scene/model3d/connection';
import { TextureAreaBorderParameters } from '@3d-scene/model/texture-area-border-parameters';
import { ArticleUsageVO } from '@article/model/vo/article.vo';
import { SelectionArticleGroupIdentity } from '@project/components/article/model/vo/selection-article-group-identity';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { UserVO } from '@user/model/vo/user.vo';
import { Injectable } from '@angular/core';
import { ProjectModel } from '../model/project.model';
import { SequenceOperation } from '@shared/undo/operation/sequence-operation';

@Injectable({ providedIn: 'root' })
export class ProjectHelper implements IProjectHelper {

  private _initialized = false;
  private _projectHelpers: IResponsibleForProjectTypeHelper[] = [];
  private _defaultHelper: IProjectHelper;

  constructor(private projectModel: ProjectModel
  ) {}

  public initialize(projectHelpers: IResponsibleForProjectTypeHelper[], defaultHelper: IProjectHelper) {
    this._projectHelpers = projectHelpers;
    this._defaultHelper = defaultHelper;
    this._initialized = true;
  }

  public createSelection(articleUsage: ArticleUsageVO): SelectionArticleGroupIdentity {
    return this.getHelperInstance().createSelection(articleUsage);
  }

  public isCompositePartArticleModificationAllowed(articleContainer: ArticleContainer): boolean {
    return this.getHelperInstance().isCompositePartArticleModificationAllowed(articleContainer);
  }

  public isCompositePartArticleSelectionAllowed(articleContainer: ArticleContainer): boolean {
    return this.getHelperInstance().isCompositePartArticleSelectionAllowed(articleContainer);
  }

  public createTextureAreaBorder(textureCube: Connection): TextureAreaBorderParameters {
    return this.getHelperInstance().createTextureAreaBorder(textureCube);
  }

  public async deleteArticles(articleUsageIds: string[], callback: (articleUsageIdsToRemove: string[]) => Promise<SequenceOperation>): Promise<void> {
    await this.getHelperInstance().deleteArticles(articleUsageIds, callback);
  }

  public isMoveArticleAllowed(articleContainer: ArticleContainer): boolean {
    return this.getHelperInstance().isMoveArticleAllowed(articleContainer);
  }

  public isInitialRotationRequired(articleContainer: ArticleContainer): boolean {
    return this.getHelperInstance().isInitialRotationRequired(articleContainer);
  }

  public isReplaceWithDifferentWidthAllowed(): boolean {
    return this.getHelperInstance().isReplaceWithDifferentWidthAllowed();
  }

  public isCopyPasteAllowed(): boolean {
    return this.getHelperInstance().isCopyPasteAllowed();
  }

  public isDisplayOfProjectStructureEnabled(user: UserVO = null): boolean {
    return this.getHelperInstance().isDisplayOfProjectStructureEnabled(user);
  }
  public considerOriginalAppliedRotationForReplacement(): boolean {
    return this.getHelperInstance().considerOriginalAppliedRotationForReplacement();
  }

  public isRotateArticleAllowed(articleContainer: ArticleContainer): boolean {
    return this.getHelperInstance().isRotateArticleAllowed(articleContainer);
  }

  public get showWireFramesForHiddenArticles(): boolean {
    return this.getHelperInstance().showWireFramesForHiddenArticles;
  }

  public set showWireFramesForHiddenArticles(value: boolean) {
    this.getHelperInstance().showWireFramesForHiddenArticles = value;
  }

  public isLabelingCorrectionDisabled(): boolean {
    return this.getHelperInstance().isLabelingCorrectionDisabled();
  }

  private getHelperInstance(): IProjectHelper {
    this.EnsureInitialized();
    const responsibleHelper = this._projectHelpers.find(projectHelper => projectHelper.isResponsible(this.projectModel.currentProjectType));
    return responsibleHelper ?? this._defaultHelper;
  }

  private EnsureInitialized() {
    if (!this._initialized) {
      throw new Error("Project Helper not initialized!")
    }
  }
}
